<template>
  <div :class="['c-base-select', classes, typeof variant !== 'undefined' ? `c-base-select--${variant}` : null, typeof size !== 'undefined' ? `c-base-select--size-${size}` : null]">
    <v-select
      :options="options"
      :searchable="isSearchable"
      :clearable="isClearable"
      :create-option="createOption"
      :reduce="item => item.value"
      :placeholder="placeholder"
      :append-to-body="true"
      :calculate-position="withPopper"
      :taggable="isTaggable"
      :loading="isLoading"
      :title="selected && options.some(option => option.value === selected) ? options.find(option => option.value === selected).label : selected"
      v-model="selected"
      v-bind="$attrs"
      v-on="listeners">
      <template v-if="iconPrepend" v-slot:selected-option="{ label }">
        <div class="c-base-select__input-icon-prepend" >
          <font-awesome-icon :icon="iconPrepend"/>
        </div>
        {{ label }}
      </template>
      <template v-slot:open-indicator="{ attributes }">
        <span v-bind="attributes"><font-awesome-icon :icon="['fal', 'chevron-down']"/></span>
      </template>
      <template v-slot:spinner="{ loading }">
        <div v-if="loading">
          <base-loading :is-active="loading || false" :can-show-label="false" is-small/>
        </div>
      </template>
    </v-select>
  </div>
</template>

<script>
import vSelect from 'vue-select'

import { createPopper } from '@popperjs/core'

export default {
  name: 'BaseSelect',

  components: { vSelect },

  props: {
    value: {
      type: [String, Number, null]
    },
    defaultPosition: {
      type: String,
      default: 'bottom' // top or bottom
    },
    isClearable: {
      type: Boolean,
      default: false
    },
    isTaggable: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isSearchable: {
      type: Boolean,
      default: true
    },
    options: {
      type: Array,
      required: true
    },
    state: {
      type: [Boolean, null],
      default: null
    },
    variant: {
      type: String,
      required: false
    },
    placeholder: {
      type: String,
      required: false
    },
    iconPrepend: {
      type: Array,
      required: false
    },
    isButton: {
      type: Boolean,
      default: false
    },
    inBaseTitle: {
      type: Boolean,
      default: false
    },
    hasShadow: {
      type: Boolean,
      default: false
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: undefined,
      validator: value => ['sm'].includes(value)
    },
    createOption: {
      type: Function,
      default (option) {
        return { [this.label]: option, value: option }
      }
    },
    dropdownMenuClass: {
      type: String,
      required: false
    }
  },

  computed: {
    classes () {
      return {
        'c-base-select--is-button': this.isButton === true,
        'c-base-select--is-valid': this.state === true,
        'c-base-select--is-invalid': this.state === false,
        'c-base-select--is-in-base-title': this.inBaseTitle === true,
        'c-base-select--has-shadow': this.hasShadow === true,
        'c-base-select--has-icon-prepend': this.iconPrepend,
        'c-base-select--hide-actions': this.hideActions
      }
    },

    listeners () {
      const { input, ...listeners } = this.$listeners
      return listeners
    },

    selected: {
      get () {
        return this.value
      },
      set (value) {
        if (value !== this.value && (value !== null && !this.isTaggable ? this.options.find(option => option.value === value) : true)) {
          this.$emit('input', value)
        }
      }
    }
  },

  methods: {
    withPopper (dropdownList, component, { width }) {
      /**
       * We need to explicitly define the dropdown width since
       * it is usually inherited from the parent with CSS.
       */
      dropdownList.style.width = width

      if (this.dropdownMenuClass) {
        dropdownList.classList.add(this.dropdownMenuClass)
      }

      /**
       * Here we position the dropdownList relative to the $refs.toggle Element.
       *
       * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
       * the dropdownList overlap by 1 pixel.
       *
       * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
       * wrapper so that we can set some styles for when the dropdown is placed
       * above.
       */
      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: this.defaultPosition,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -1]
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn ({ state }) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            }
          }]
      })

      /**
       * To prevent memory leaks Popper needs to be destroyed.
       * If you return function, it will be called just before dropdown is removed from DOM.
       */
      return () => popper.destroy()
    }
  }
}
</script>
